import { Icon } from "@iconify/react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { singleImageUpload } from "../../Hooks/ImageUpload";
import Loading from "../../Shared/Loading";

const SuperAdminProductEdit = () => {
  const { id } = useParams();
  const [product, setProduct] = useState({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchProduct() {
      try {
        const response = await axios.get(
          `https://servercareseebd.careseebd.com/api/v1/products/${id}`
        );
        setProduct(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }

    fetchProduct();
  }, [id]);

  const handleEditClick = () => {
    setEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      await axios.put(`https://servercareseebd.careseebd.com/api/v1/products/${id}`, product);
      toast.success("Product updated successfully");
      setEditing(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct({
      ...product,
      [name]: value,
    });
  };

  const handleChangeUploadImage = async (event) => {
    const image = event.target.files[0];
    const formData = new FormData();
    formData.append("image", image);
    singleImageUpload(formData, (url) =>
      setProduct({ ...product, imageUrl: url })
    );
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center items-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6 text-rose-700 text-sm font-bold flex items-center gap-3">
        <Icon icon="solar:danger-bold" className="w-5 h-5" />
        Error loading product: {error}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto select-none space-y-6">
      {/* Header card */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:box-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">
            {editing ? "Modify Product Details" : "Product Specifications"}
          </h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">
            {editing ? "Update details for the product item" : "Overview of product properties and media"}
          </p>
        </div>
      </div>

      {editing ? (
        <form onSubmit={(e) => e.preventDefault()} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden p-6 sm:p-8 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Product Name */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Product Name</label>
              <input
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm font-medium"
                type="text"
                name="name"
                value={product.name || ""}
                onChange={handleInputChange}
              />
            </div>

            {/* Product Image */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Replace Image</label>
              <input
                type="file"
                className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-[10px] file:font-extrabold file:uppercase file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 transition-all cursor-pointer"
                onChange={handleChangeUploadImage}
                accept="image/*"
              />
            </div>

            {/* Price */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Price ($)</label>
              <input
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm font-medium"
                type="number"
                name="price"
                value={product.price || 0}
                onChange={handleInputChange}
              />
            </div>

            {/* Stock */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Stock quantity</label>
              <input
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm font-medium"
                type="number"
                name="stock"
                value={product.stock || 0}
                onChange={handleInputChange}
              />
            </div>

            {/* Categories */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Categories</label>
              <input
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm font-medium"
                type="text"
                name="categories"
                value={product.categories || ""}
                onChange={handleInputChange}
              />
            </div>

            {/* Weight */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Weight (kg)</label>
              <input
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm font-medium"
                type="number"
                step="any"
                name="weight"
                value={product.weight || 0}
                onChange={handleInputChange}
              />
            </div>

            {/* Dimensions */}
            <div className="space-y-1.5 sm:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Dimensions</label>
              <input
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm font-medium"
                type="text"
                name="dimensions"
                value={product.dimensions || ""}
                onChange={handleInputChange}
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5 sm:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">Description</label>
              <textarea
                name="description"
                value={product.description || ""}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm font-medium"
              />
            </div>
          </div>

          {/* Action triggers */}
          <div className="pt-4 border-t border-slate-50 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-5 py-2.5 border border-slate-200 text-slate-500 font-extrabold text-xs rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveClick}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-xs rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
            >
              <Icon icon="solar:diskette-bold" className="w-4 h-4" />
              <span>Save Changes</span>
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden p-6 sm:p-8 flex flex-col md:flex-row justify-between gap-8">
          <div className="flex-1 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Product Name</p>
                <p className="text-base font-extrabold text-slate-800 mt-1">{product.name}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Price</p>
                <p className="text-base font-black text-indigo-600 mt-1">${product.price}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Stock Status</p>
                <p className="text-sm font-bold mt-1">
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] ${product.stock > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
                    {product.stock} units available
                  </span>
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Category</p>
                <p className="text-sm font-semibold text-slate-700 mt-1">
                  <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[10px] uppercase tracking-wide">
                    {product.categories || "Unassigned"}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Weight</p>
                <p className="text-sm font-medium text-slate-650 mt-1">{product.weight ? `${product.weight} kg` : "N/A"}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Dimensions</p>
                <p className="text-sm font-medium text-slate-650 mt-1">{product.dimensions || "N/A"}</p>
              </div>
            </div>
            <div className="pt-4 border-t border-slate-55">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Description</p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{product.description || "No description provided."}</p>
            </div>
            <div className="pt-4">
              <button
                type="button"
                onClick={handleEditClick}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-xs rounded-xl shadow-md shadow-indigo-600/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                <Icon icon="basil:edit-outline" className="w-4 h-4" />
                <span>Edit Product</span>
              </button>
            </div>
          </div>
          {product.imageUrl && (
            <div className="w-full md:w-64 h-64 rounded-2xl overflow-hidden border border-slate-100 shadow-sm bg-slate-50 flex items-center justify-center p-4">
              <img
                className="max-w-full max-h-full object-contain rounded-xl"
                src={product.imageUrl}
                alt={product.name}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminProductEdit;
